import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend
} from 'recharts';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  Boxes, TrendingUp, DollarSign, Package, Calendar, RefreshCw, 
  Download, FileText, ChevronRight, ArrowUpRight, ArrowDownRight,
  Building2, Truck, Filter, Eye, BarChart3, PieChart as PieChartIcon,
  Table as TableIcon, FileSpreadsheet, Printer, Search, X
} from 'lucide-react';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#ec4889', '#3b82f6', '#eab308', '#14b8a6', '#8b5cf6'];

const AreaConsumption = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [period, setPeriod] = useState('month');
  const [customDate, setCustomDate] = useState({ start: '', end: '' });
  const tabsContainerRef = useRef(null);

  // Helper functions - header is sticky so tabs always visible
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
  };
  
  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [areas, setAreas] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaDetail, setAreaDetail] = useState(null);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [supplyDetail, setSupplyDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSummary();
    fetchAreas();
    fetchSupplies();
  }, [period, customDate.start, customDate.end, currentBranch]);

  const buildParams = () => {
    let params = `period=${period}`;
    if (period === 'custom' && customDate.start && customDate.end) {
      params += `&start_date=${customDate.start}&end_date=${customDate.end}`;
    }
    // Add branch filter
    if (currentBranch) {
      params += `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}`;
    }
    return params;
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/area-consumption/summary?${buildParams()}&t=${Date.now()}`);
      setSummaryData(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await api.get(`/reports/area-consumption/areas?${buildParams()}&t=${Date.now()}`);
      setAreas(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSupplies = async () => {
    try {
      const res = await api.get(`/reports/area-consumption/supplies?${buildParams()}&t=${Date.now()}`);
      setSupplies(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAreaDetail = async (areaId) => {
    try {
      const res = await api.get(`/reports/area-consumption/by-area/${areaId}?${buildParams()}&t=${Date.now()}`);
      setAreaDetail(res.data);
      setSelectedArea(areaId);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar detalle del área');
    }
  };

  const fetchSupplyDetail = async (supplyId) => {
    try {
      const res = await api.get(`/reports/area-consumption/by-supply/${supplyId}?${buildParams()}&t=${Date.now()}`);
      setSupplyDetail(res.data);
      setSelectedSupply(supplyId);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar detalle del insumo');
    }
  };

  const exportPDF = async () => {
    try {
      toast.info('Generando PDF...');
      const response = await api.get(`/reports/area-consumption/export/pdf?${buildParams()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consumo_area_${period}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const filteredSupplies = supplies.filter(s => 
    s.supply_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 pt-16 md:pt-6">
        <div className="max-w-7xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header - Sticky */}
          <div className="mb-6 sticky top-0 z-40 bg-slate-50 pb-2">
            <h1 className="text-2xl md:text-3xl font-chivo font-extrabold flex items-center gap-3" data-testid="area-consumption-title">
              <Building2 className="w-8 h-8 text-accent" />
              Consumo por Área
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Análisis detallado de traspasos y consumo para planificación de compras
            </p>
          </div>

          {/* Period Filter */}
          <Card className="mb-6 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex bg-slate-100 rounded-xl p-1.5">
                  {[
                    { id: 'today', label: 'Hoy' },
                    { id: 'week', label: 'Semana' },
                    { id: 'month', label: 'Mes' },
                    { id: 'year', label: 'Año' },
                  ].map(({ id, label }) => (
                    <button
                      type="button"
                      key={id}
                      onClick={(e) => { e.preventDefault(); handlePeriodChange(id); }}
                      data-testid={`period-${id}`}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                        period === id
                          ? 'bg-accent text-white shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Input
                    type="date"
                    value={customDate.start}
                    onChange={(e) => { setCustomDate(prev => ({ ...prev, start: e.target.value })); setPeriod('custom'); }}
                    className="w-auto h-9"
                  />
                  <span className="text-muted-foreground">a</span>
                  <Input
                    type="date"
                    value={customDate.end}
                    onChange={(e) => { setCustomDate(prev => ({ ...prev, end: e.target.value })); setPeriod('custom'); }}
                    className="w-auto h-9"
                  />
                  <Button variant="outline" size="icon" onClick={fetchSummary} disabled={loading} className="h-9 w-9">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div ref={tabsContainerRef} className="flex gap-2 mb-6 bg-white rounded-xl p-1.5 shadow-sm w-fit">
            {[
              { id: 'summary', label: 'Resumen', icon: PieChartIcon },
              { id: 'by-area', label: 'Por Área', icon: Building2 },
              { id: 'by-supply', label: 'Por Insumo', icon: Package },
              { id: 'export', label: 'Exportar', icon: Download }
            ].map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                onClick={(e) => { e.preventDefault(); handleTabChange(id); }}
                data-testid={`tab-${id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === id
                    ? 'bg-accent text-white shadow-md'
                    : 'text-muted-foreground hover:text-foreground hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {loading && !summaryData ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-10 h-10 animate-spin text-accent" />
            </div>
          ) : (
            <>
              {/* ============ SUMMARY TAB ============ */}
              {activeTab === 'summary' && summaryData && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-orange-700">Total Traspasos</p>
                            <p className="text-3xl font-extrabold text-orange-800">{summaryData.total_transfers}</p>
                          </div>
                          <Truck className="w-10 h-10 text-orange-500/50" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-700">Costo Total</p>
                            <p className="text-3xl font-extrabold text-green-800">{formatCurrency(summaryData.total_cost)}</p>
                          </div>
                          <DollarSign className="w-10 h-10 text-green-500/50" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-700">Unidades Totales</p>
                            <p className="text-3xl font-extrabold text-blue-800">{summaryData.total_units?.toFixed(1)}</p>
                          </div>
                          <Boxes className="w-10 h-10 text-blue-500/50" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pie Chart - Cost by Area */}
                    <Card className="shadow-sm border-0">
                      <CardHeader>
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <PieChartIcon className="w-5 h-5 text-accent" />
                          Distribución de Costo por Área
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {summaryData.by_area.length > 0 ? (
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={summaryData.by_area}
                                  dataKey="total_cost"
                                  nameKey="area_name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={80}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                  labelLine={false}
                                >
                                  {summaryData.by_area.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-10">Sin datos</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Areas List */}
                    <Card className="shadow-sm border-0">
                      <CardHeader>
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-accent" />
                          Consumo por Área
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {summaryData.by_area.map((area, idx) => (
                          <div 
                            key={area.area_id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all"
                            onClick={() => { setActiveTab('by-area'); fetchAreaDetail(area.area_id); }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                              <div>
                                <p className="font-semibold">{area.area_name}</p>
                                <p className="text-xs text-muted-foreground">{area.transfer_count} traspasos • {area.supply_count} insumos</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-accent">{formatCurrency(area.total_cost)}</p>
                              <p className="text-xs text-muted-foreground">{area.percentage}%</p>
                            </div>
                          </div>
                        ))}
                        {summaryData.by_area.length === 0 && (
                          <p className="text-center text-muted-foreground py-6">No hay traspasos en este período</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Daily Trend */}
                  {summaryData.daily_trend.length > 0 && (
                    <Card className="shadow-sm border-0">
                      <CardHeader>
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          Tendencia Diaria de Consumo
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={summaryData.daily_trend}>
                              <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                              <Tooltip formatter={(value, name) => [name === 'cost' ? formatCurrency(value) : value, name === 'cost' ? 'Costo' : 'Unidades']} />
                              <Bar dataKey="cost" name="Costo" fill="#f97316" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Top Supplies */}
                  <Card className="shadow-sm border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Package className="w-5 h-5 text-accent" />
                        Top Insumos Consumidos
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 font-semibold">#</th>
                              <th className="text-left p-3 font-semibold">Insumo</th>
                              <th className="text-center p-3 font-semibold">Unidad</th>
                              <th className="text-right p-3 font-semibold">Cantidad</th>
                              <th className="text-right p-3 font-semibold">Costo Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryData.top_supplies.slice(0, 10).map((supply, idx) => (
                              <tr key={supply.supply_id} className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx < 3 ? 'bg-accent text-white' : 'bg-slate-200'}`}>
                                    {idx + 1}
                                  </span>
                                </td>
                                <td className="p-3 font-medium">{supply.supply_name}</td>
                                <td className="p-3 text-center">{supply.unit}</td>
                                <td className="p-3 text-right">{supply.total_quantity?.toFixed(2)}</td>
                                <td className="p-3 text-right font-bold text-accent">{formatCurrency(supply.total_cost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ============ BY AREA TAB ============ */}
              {activeTab === 'by-area' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Areas List */}
                  <Card className="shadow-sm border-0 lg:col-span-1">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold">Áreas</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                      {areas.map((area) => (
                        <div
                          key={area.area_id}
                          onClick={() => fetchAreaDetail(area.area_id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedArea === area.area_id 
                              ? 'bg-accent text-white' 
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-semibold">{area.area_name}</p>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                          <div className={`text-xs mt-1 ${selectedArea === area.area_id ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {area.transfer_count} traspasos • {formatCurrency(area.total_cost)}
                          </div>
                        </div>
                      ))}
                      {areas.length === 0 && (
                        <p className="text-center text-muted-foreground py-6">No hay áreas con consumo</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Area Detail */}
                  <Card className="shadow-sm border-0 lg:col-span-2">
                    {areaDetail ? (
                      <>
                        <CardHeader className="bg-gradient-to-r from-accent to-orange-600 text-white rounded-t-lg">
                          <CardTitle className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <Building2 className="w-5 h-5" />
                              {areaDetail.area_name}
                            </span>
                            <span className="text-2xl font-extrabold">{formatCurrency(areaDetail.total_cost)}</span>
                          </CardTitle>
                          <p className="text-white/80 text-sm">
                            {areaDetail.total_transfers} traspasos • {areaDetail.total_units?.toFixed(2)} unidades
                          </p>
                        </CardHeader>
                        <CardContent className="p-4">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            Insumos Consumidos
                          </h4>
                          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-white">
                                <tr className="border-b">
                                  <th className="text-left p-2 font-semibold">Insumo</th>
                                  <th className="text-right p-2 font-semibold">Cantidad</th>
                                  <th className="text-right p-2 font-semibold">Costo Unit.</th>
                                  <th className="text-right p-2 font-semibold">Total</th>
                                  <th className="text-right p-2 font-semibold">Proy. Mensual</th>
                                </tr>
                              </thead>
                              <tbody>
                                {areaDetail.supplies.map((supply) => (
                                  <tr key={supply.supply_id} className="border-b hover:bg-slate-50">
                                    <td className="p-2 font-medium">{supply.supply_name}</td>
                                    <td className="p-2 text-right">{supply.total_quantity} {supply.unit}</td>
                                    <td className="p-2 text-right">{formatCurrency(supply.cost_per_unit)}</td>
                                    <td className="p-2 text-right font-bold text-accent">{formatCurrency(supply.total_cost)}</td>
                                    <td className="p-2 text-right text-muted-foreground">{supply.projected_monthly} {supply.unit}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </>
                    ) : (
                      <CardContent className="flex items-center justify-center h-[400px] text-muted-foreground">
                        <div className="text-center">
                          <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>Selecciona un área para ver el detalle</p>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                </div>
              )}

              {/* ============ BY SUPPLY TAB ============ */}
              {activeTab === 'by-supply' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Supplies List */}
                  <Card className="shadow-sm border-0 lg:col-span-1">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold">Insumos</CardTitle>
                      <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Buscar insumo..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                      {filteredSupplies.map((supply) => (
                        <div
                          key={supply.supply_id}
                          onClick={() => fetchSupplyDetail(supply.supply_id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedSupply === supply.supply_id 
                              ? 'bg-accent text-white' 
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-semibold truncate">{supply.supply_name}</p>
                            <ChevronRight className="w-4 h-4 flex-shrink-0" />
                          </div>
                          <div className={`text-xs mt-1 ${selectedSupply === supply.supply_id ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {supply.total_quantity?.toFixed(2)} {supply.unit} • {formatCurrency(supply.total_cost)}
                          </div>
                        </div>
                      ))}
                      {filteredSupplies.length === 0 && (
                        <p className="text-center text-muted-foreground py-6">No hay insumos</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Supply Detail */}
                  <Card className="shadow-sm border-0 lg:col-span-2">
                    {supplyDetail ? (
                      <>
                        <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-lg">
                          <CardTitle className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <Package className="w-5 h-5" />
                              {supplyDetail.supply_name}
                            </span>
                            <span className="text-2xl font-extrabold">{formatCurrency(supplyDetail.total_cost)}</span>
                          </CardTitle>
                          <p className="text-white/80 text-sm">
                            {supplyDetail.total_quantity?.toFixed(2)} {supplyDetail.unit} • ${supplyDetail.cost_per_unit?.toFixed(2)}/{supplyDetail.unit}
                          </p>
                        </CardHeader>
                        <CardContent className="p-4 space-y-4">
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              Consumo por Área
                            </h4>
                            <div className="space-y-2">
                              {supplyDetail.by_area.map((area, idx) => (
                                <div key={area.area_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                    <span className="font-medium">{area.area_name}</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold">{area.total_quantity?.toFixed(2)} {supplyDetail.unit}</p>
                                    <p className="text-xs text-muted-foreground">{area.percentage}% del total</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Monthly Trend */}
                          {supplyDetail.monthly_trend.length > 1 && (
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Tendencia Mensual
                              </h4>
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={supplyDetail.monthly_trend}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="quantity" stroke="#6366f1" strokeWidth={2} name="Cantidad" />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </>
                    ) : (
                      <CardContent className="flex items-center justify-center h-[400px] text-muted-foreground">
                        <div className="text-center">
                          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>Selecciona un insumo para ver el detalle</p>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                </div>
              )}

              {/* ============ EXPORT TAB ============ */}
              {activeTab === 'export' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="shadow-sm border-0">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-red-500" />
                        Exportar a PDF
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Genera un reporte PDF profesional con el resumen de consumo por área, 
                        incluyendo totales, distribución por área y top insumos.
                      </p>
                      <Button onClick={exportPDF} className="w-full bg-red-500 hover:bg-red-600">
                        <Download className="w-4 h-4 mr-2" />
                        Descargar PDF
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="shadow-sm border-0">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Printer className="w-5 h-5 text-blue-500" />
                        Imprimir Reporte
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Abre una ventana de impresión para imprimir el reporte actual 
                        directamente desde el navegador.
                      </p>
                      <Button onClick={() => window.print()} variant="outline" className="w-full">
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimir
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Quick Stats for Export */}
                  {summaryData && (
                    <Card className="shadow-sm border-0 md:col-span-2">
                      <CardHeader>
                        <CardTitle>Vista Previa del Reporte</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-2xl font-bold text-accent">{summaryData.total_transfers}</p>
                              <p className="text-xs text-muted-foreground">Traspasos</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryData.total_cost)}</p>
                              <p className="text-xs text-muted-foreground">Costo Total</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-blue-600">{summaryData.total_units?.toFixed(1)}</p>
                              <p className="text-xs text-muted-foreground">Unidades</p>
                            </div>
                          </div>
                          <div className="border-t pt-4">
                            <p className="font-semibold mb-2">Incluye:</p>
                            <ul className="text-sm text-muted-foreground space-y-1">
                              <li>• Resumen general de traspasos</li>
                              <li>• Distribución de costo por área</li>
                              <li>• Top 10 insumos consumidos</li>
                              <li>• Período: {summaryData.period?.label}</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AreaConsumption;
